(() => {
  // Force FR formatting for unit prices (store is FR/EUR).
  const getLocale = () => 'fr-FR';
  const getCurrency = () => document.documentElement?.dataset?.currency || window.Shopify?.currency?.active || 'EUR';

  const formatMoney = (cents) => {
    const value = Number(cents || 0) / 100;
    try {
      return new Intl.NumberFormat(getLocale(), {
        style: 'currency',
        currency: getCurrency(),
        maximumFractionDigits: 2,
      }).format(value);
    } catch (e) {
      return `${value.toFixed(2)} ${getCurrency()}`;
    }
  };

  const formatUnitPrice = (unitValue, unitLabel) => {
    const formatted = new Intl.NumberFormat(getLocale(), {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(unitValue);
    return `${formatted} ${unitLabel}`;
  };

  const enhanceTierPicker = (root) => {
    const tierPicker = root.querySelector?.('[data-ep-tier-picker]');
    if (!tierPicker) return;
    const commercialOffer = root.querySelector?.('[data-ep-commercial-offer]');
    const tierUnitLabel = tierPicker.dataset.epTierUnitLabel || '€/pièce';
    const tierSavingUnit = tierPicker.dataset.epTierSavingUnit || 'pièce';

    // Avoid double init (Dawn swaps <product-info> on variant changes).
    if (tierPicker.dataset.epTierEnhanced === 'true') return;
    tierPicker.dataset.epTierEnhanced = 'true';

    const variantSelects = tierPicker.closest('variant-selects');
    const variantsJson = variantSelects?.querySelector('[data-ep-tier-variants]');
    let variants = [];
    if (variantsJson?.textContent) {
      try {
        variants = JSON.parse(variantsJson.textContent);
      } catch (e) {
        variants = [];
      }
    }

    const getOptionGroups = () => {
      if (!variantSelects) return [];
      return Array.from(variantSelects.querySelectorAll('[data-ep-option-position]'));
    };

    const getSelectedValue = (group) => {
      if (!group) return null;
      if (group.matches('[data-ep-tier-picker]')) {
        const checked = group.querySelector('input[type="radio"]:checked');
        return checked?.value || null;
      }
      const select = group.querySelector('select');
      if (select) {
        return select.value || null;
      }
      const checked = group.querySelector('input[type="radio"]:checked');
      if (checked) return checked.value || null;
      return null;
    };

    const getSelections = () => {
      const selections = {};
      getOptionGroups().forEach((group) => {
        const pos = Number(group.dataset.epOptionPosition || 0);
        if (!pos) return;
        const value = getSelectedValue(group);
        if (value != null) selections[pos] = value;
      });
      return selections;
    };

    const getVariantForRow = (row) => {
      if (!row || !variants.length) return null;
      const tierPos = Number(tierPicker.dataset.epTierOptionPosition || 0);
      if (!tierPos) return null;
      const selections = getSelections();
      selections[tierPos] = row.dataset.epTierValue || '';
      return variants.find((variant) => {
        if (!variant) return false;
        if (selections[1] && variant.option1 !== selections[1]) return false;
        if (selections[2] && variant.option2 !== selections[2]) return false;
        if (selections[3] && variant.option3 !== selections[3]) return false;
        return true;
      }) || null;
    };

    const getSelectedRow = () => {
      const checkedInput = tierPicker.querySelector('input[type="radio"]:checked');
      return checkedInput?.closest('.ep-tier-picker__item')?.querySelector('[data-ep-tier-row]');
    };

    const getTierRows = () => Array.from(tierPicker.querySelectorAll('[data-ep-tier-row]'));
    const getTierItems = () => Array.from(tierPicker.querySelectorAll('.ep-tier-picker__item'));

    const updateVisibleEdgeClasses = () => {
      const visibleItems = getTierItems().filter((item) => !item.hidden);

      getTierItems().forEach((item) => {
        item.classList.remove('is-first-visible', 'is-last-visible');
      });

      if (!visibleItems.length) return;

      visibleItems[0].classList.add('is-first-visible');
      visibleItems[visibleItems.length - 1].classList.add('is-last-visible');
    };

    const sortVisibleRows = () => {
      const table = tierPicker.querySelector('.ep-tier-table');
      if (!table) return;

      const contactRow = table.querySelector('.ep-tier-more--link');
      const sortedItems = getTierItems()
        .filter((item) => !item.hidden)
        .sort((a, b) => {
          const qtyA = Number(a.querySelector('[data-ep-tier-row]')?.dataset.epTierQty || 0);
          const qtyB = Number(b.querySelector('[data-ep-tier-row]')?.dataset.epTierQty || 0);
          return qtyA - qtyB;
        });

      sortedItems.forEach((item) => {
        if (contactRow) {
          table.insertBefore(item, contactRow);
        } else {
          table.appendChild(item);
        }
      });

      updateVisibleEdgeClasses();
    };

    const setRowHint = (row, label = '', value = '', context = '') => {
      if (!row) return;
      const hintEl = row.querySelector('[data-ep-tier-hint]');
      if (!hintEl) return;

      const labelText = String(label || '').trim();
      const valueText = String(value || '').trim();
      const contextText = String(context || '').trim();
      const labelEl = hintEl.querySelector('[data-ep-tier-hint-label]');
      const valueEl = hintEl.querySelector('[data-ep-tier-hint-value]');
      const contextEl = hintEl.querySelector('[data-ep-tier-hint-context]');
      if (labelEl || valueEl || contextEl) {
        if (labelEl) labelEl.textContent = labelText;
        if (valueEl) valueEl.textContent = valueText;
        if (contextEl) contextEl.textContent = contextText;
      } else {
        hintEl.textContent = [labelText, valueText].filter(Boolean).join(' ');
      }
      const hasVisibleText = Boolean(labelText || valueText);
      hintEl.hidden = !hasVisibleText;
      hintEl.classList.toggle('ep-tier-row__hint--saving', Boolean(valueText));
      hintEl.classList.toggle('ep-tier-row__hint--reference', Boolean(labelText && !valueText));
      row.classList.toggle('has-hint', hasVisibleText);
    };

    const setFormQuantity = () => {
      const qtyInput = tierPicker.querySelector('[data-ep-tier-quantity-input]');
      if (!qtyInput) return;

      const selectedRow = getSelectedRow();
      const qty = Number(selectedRow?.dataset.epTierQty || 0);
      // Fallback to 1 to avoid adding "0" quantity.
      qtyInput.value = qty > 0 ? String(qty) : '1';
    };

    const updateRows = () => {
      const rows = getTierRows();
      let firstVisibleInput = null;

      rows.forEach((row) => {
        const item = row.closest('.ep-tier-picker__item');
        const input = item?.querySelector('input[type="radio"]');
        const unitEl = row.querySelector('[data-ep-tier-unit]');
        const totalEl = row.querySelector('[data-ep-tier-total]');
        if (!unitEl) return;
        const qty = Number(row.dataset.epTierQty || 0);
        const variant = getVariantForRow(row);
        const unitCents = variants.length
          ? Number(variant?.price || 0)
          : Number(row.dataset.epTierUnitCents || 0);
        row.dataset.epTierUnitCents = unitCents;
        row.dataset.epTierTotalCents = qty ? unitCents * qty : 0;

        if (variants.length && (!variant || !variant.available)) {
          if (input) {
            input.disabled = true;
            input.classList.add('disabled');
          }
          if (item) item.hidden = true;
          unitEl.textContent = '—';
          if (totalEl) totalEl.textContent = '—';
          setRowHint(row, '');
          return;
        }

        if (input) {
          input.disabled = false;
          input.classList.remove('disabled');
          if (!firstVisibleInput) firstVisibleInput = input;
        }
        if (item) item.hidden = false;
        setRowHint(row, '');

        if (qty && unitCents) {
          unitEl.textContent = formatUnitPrice(unitCents / 100, tierUnitLabel);
          if (totalEl) totalEl.textContent = formatMoney(unitCents * qty);
        }
      });

      const checked = tierPicker.querySelector('input[type="radio"]:checked');
      if ((checked?.disabled || checked?.closest('.ep-tier-picker__item')?.hidden) && firstVisibleInput && firstVisibleInput !== checked) {
        firstVisibleInput.checked = true;
        firstVisibleInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      sortVisibleRows();
    };

    const updateCommercialOffer = () => {
      if (!commercialOffer) return;

      const tiers = getTierRows()
        .filter((row) => !row.closest('.ep-tier-picker__item')?.hidden)
        .map((row) => ({
          qty: Number(row.dataset.epTierQty || 0),
          unitCents: Number(row.dataset.epTierUnitCents || 0),
        }))
        .filter((tier) => tier.qty > 0 && tier.unitCents > 0)
        .sort((a, b) => a.qty - b.qty);

      if (!tiers.length) return;

      const entryTier = tiers[0];
      const bestTier = tiers.reduce((best, tier) => {
        if (tier.unitCents < best.unitCents) return tier;
        if (tier.unitCents === best.unitCents && tier.qty < best.qty) return tier;
        return best;
      }, tiers[0]);

      const locale = commercialOffer.dataset.epOfferLocale || getLocale();
      const quantityUnit = commercialOffer.dataset.epOfferQuantityUnit || '';
      const fromLabel = commercialOffer.dataset.epOfferFromLabel || '';
      const entryFromLabel = commercialOffer.dataset.epOfferEntryFromLabel || '';
      const formatQuantity = (quantity) => new Intl.NumberFormat(locale).format(quantity);

      const bestPriceEl = commercialOffer.querySelector('[data-ep-offer-best-price]');
      const bestQtyEl = commercialOffer.querySelector('[data-ep-offer-best-quantity]');
      const entryPriceEl = commercialOffer.querySelector('[data-ep-offer-entry-price]');
      const entryQtyEl = commercialOffer.querySelector('[data-ep-offer-entry-quantity]');
      const configEl = commercialOffer.querySelector('[data-ep-offer-configuration]');
      const badgeEl = commercialOffer.querySelector('[data-ep-offer-badge]');
      const entryEl = commercialOffer.querySelector('[data-ep-offer-entry]');
      const hasVolumeSavings = bestTier.unitCents < entryTier.unitCents;
      const conditionLabel = hasVolumeSavings ? fromLabel : entryFromLabel;
      const volumeBadge = commercialOffer.dataset.epOfferVolumeBadge || '';
      const flatBadge = commercialOffer.dataset.epOfferFlatBadge || '';

      if (bestPriceEl) bestPriceEl.textContent = formatMoney(bestTier.unitCents);
      if (bestQtyEl) bestQtyEl.textContent = `${conditionLabel} ${formatQuantity(bestTier.qty)} ${quantityUnit}`.trim();
      if (entryPriceEl) entryPriceEl.textContent = formatMoney(entryTier.unitCents);
      if (entryQtyEl) entryQtyEl.textContent = `${entryFromLabel} ${formatQuantity(entryTier.qty)} ${quantityUnit}`.trim();
      if (badgeEl) badgeEl.textContent = hasVolumeSavings ? volumeBadge : flatBadge;
      if (entryEl) entryEl.hidden = !hasVolumeSavings;
      commercialOffer.classList.toggle('ep-commercial-offer--flat-price', !hasVolumeSavings);

      if (configEl) {
        const tierPosition = Number(tierPicker.dataset.epTierOptionPosition || 0);
        const configuration = getOptionGroups()
          .filter((group) => Number(group.dataset.epOptionPosition || 0) !== tierPosition)
          .map(getSelectedValue)
          .filter(Boolean);
        configEl.textContent = configuration.join(' · ');
      }
    };

    const updateTierSavings = () => {
      const rows = getTierRows();
      rows.forEach((row) => setRowHint(row, ''));

      const tiers = rows
        .filter((row) => {
          const item = row.closest('.ep-tier-picker__item');
          const input = item?.querySelector('input[type="radio"]');
          return !item?.hidden && !input?.disabled;
        })
        .map((row) => ({
          row,
          qty: Number(row.dataset.epTierQty || 0),
          unitCents: Number(row.dataset.epTierUnitCents || 0),
        }))
        .filter((tier) => tier.qty > 0)
        .sort((a, b) => a.qty - b.qty);

      if (!tiers.length || tiers.some((tier) => tier.unitCents <= 0)) return;

      const entryUnitCents = tiers[0].unitCents;
      if (!entryUnitCents) return;

      if (tiers.length < 2) return;

      const isMonotone = tiers.every((tier, index) => {
        if (index === 0) return true;
        return tier.unitCents <= tiers[index - 1].unitCents;
      });
      if (!isMonotone) return;

      tiers.slice(1).forEach((tier) => {
        const percentage = Math.round((entryUnitCents - tier.unitCents) / entryUnitCents * 100);
        if (percentage <= 0) return;
        setRowHint(
          tier.row,
          'ÉCONOMISEZ',
          `${percentage} %`,
          `Économie de ${percentage} % par rapport au palier de référence, prix par ${tierSavingUnit}.`,
        );
      });
    };

    tierPicker.addEventListener('change', () => {
      setFormQuantity();
      updateTierSavings();
      updateCommercialOffer();
    });
    updateRows();
    updateTierSavings();
    setFormQuantity();
    updateCommercialOffer();

    if (variantSelects) {
      variantSelects.addEventListener('change', () => {
        updateRows();
        updateTierSavings();
        setFormQuantity();
        updateCommercialOffer();
      });
    }
  };

  // Initial paint.
  document.querySelectorAll('product-info').forEach((node) => enhanceTierPicker(node));

  // Re-run after Dawn swaps <product-info> on variant changes.
  document.addEventListener('product-info:loaded', (event) => {
    enhanceTierPicker(event.target);
  });

  // Safety net: some theme updates replace inner HTML without emitting the custom event in all cases.
  // We watch for DOM changes and enhance any fresh tier picker that appears.
  const mo = new MutationObserver((mutations) => {
    for (const m of mutations) {
      for (const node of m.addedNodes) {
        if (!(node instanceof HTMLElement)) continue;
        // If the subtree contains a new tier picker, enhance the nearest product-info.
        if (node.matches?.('[data-ep-tier-picker]') || node.querySelector?.('[data-ep-tier-picker]')) {
          const productInfo = node.closest?.('product-info') || document.querySelector('product-info');
          if (productInfo) enhanceTierPicker(productInfo);
          return;
        }
      }
    }
  });
  mo.observe(document.body, { childList: true, subtree: true });
})();
